import { useEffect, useState } from 'react'
import posthog from 'posthog-js'
import { toast } from 'react-toastify'

import DotsLoading from 'components/loadings/dotsLoading'
import {
    canRequestContact,
    isMiniapp,
    loginWithSharedContact,
    requestPhoneFromMessenger,
    waitForContactLink,
} from 'modules/miniapp'
import { POST_LOGIN_PATH } from 'utilities/navigation'

interface Props {
    /** Fill the phone field WITHOUT sending anything. */
    onPrefill: (phone: string) => void
    /** Give up waiting and start the ordinary SMS login for this number. */
    onUseOtp: (phone: string) => void
    disabled?: boolean
}

type Stage = 'idle' | 'requesting' | 'waiting' | 'fallback'

// ~20 seconds. Long enough for the messenger to deliver the contact to our bot
// and for the webhook to write the link; short enough that nobody assumes the
// button is broken.
const WAIT_ATTEMPTS = 13
const WAIT_DELAY_MS = 1500

/**
 * "Use the number on this account" — one tap instead of typing eleven digits.
 *
 * Crucially this does NOT send an SMS. Sharing the number is often enough to
 * log in on its own, and firing an OTP up front would charge us for a message
 * the user never needs and drop them on a code screen they should not be on.
 *
 * Order of attempts:
 *   1. A *signed* contact payload (Eitaa, Telegram) — proven, straight in.
 *   2. The messenger delivers the contact to our BOT (Bale), which links the
 *      account behind the page — so we wait and poll for it.
 *   3. Only if both fail does the user get an explicit button to fall back to
 *      the SMS code, which is the first moment an SMS is sent.
 */
export default function ShareNumberButton({
    onPrefill,
    onUseOtp,
    disabled,
}: Props) {
    const [available, setAvailable] = useState(false)
    const [stage, setStage] = useState<Stage>('idle')
    const [phone, setPhone] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        if (!isMiniapp()) return
        canRequestContact().then((ok) => {
            if (!cancelled) setAvailable(ok)
        })
        return () => {
            cancelled = true
        }
    }, [])

    if (!available) return null

    // Full reload so every consumer of auth state picks up the new token,
    // matching what the OTP path does.
    const signedIn = () => window.location.replace(POST_LOGIN_PATH)

    const share = async () => {
        setStage('requesting')
        posthog.capture('miniapp_share_number_tapped')
        const result = await requestPhoneFromMessenger()

        // (1) Signed payload is proof — log straight in, no SMS.
        if (result?.signedResponse) {
            if (await loginWithSharedContact(result.signedResponse)) {
                return signedIn()
            }
        }

        // Fill the field so the fallback below is one tap, but send nothing.
        if (result?.phone) {
            setPhone(result.phone)
            onPrefill(result.phone)
            posthog.capture('miniapp_share_number_filled')
        }

        // (2) Wait for the contact to reach our bot and the link to appear.
        setStage('waiting')
        const linked = await waitForContactLink(WAIT_ATTEMPTS, WAIT_DELAY_MS)
        if (linked) return signedIn()

        // (3a) We never got a number, so there is nothing to offer an SMS
        // for — the ordinary form is already on screen and is the only way
        // forward. Offering a "log in by SMS" button here would be a button
        // whose sole action is to say "type your number", which is worse than
        // no button.
        if (!result?.phone) {
            posthog.capture('miniapp_share_number_declined')
            toast.info('شماره دریافت نشد؛ لطفاً شمارهٔ خود را وارد کنید.')
            setStage('idle')
            return
        }

        // (3b) We have the number but could not prove it. Offer the SMS route
        // rather than taking it for them — the only place an SMS gets sent.
        setStage('fallback')
    }

    if (stage === 'waiting' || stage === 'requesting') {
        return (
            <div className="mt-4 flex flex-col items-center gap-2 text-center">
                <DotsLoading />
                <p className="text-sm">در حال ورود به همیان…</p>
                <p className="text-xs text-text-gray leading-6">
                    شمارهٔ شما در حال بررسی است. چند لحظه صبر کنید.
                </p>
            </div>
        )
    }

    if (stage === 'fallback') {
        return (
            <div className="mt-4 flex flex-col gap-2 text-center">
                <p className="text-xs text-text-gray leading-6">
                    ورود خودکار انجام نشد. با کد پیامکی وارد شوید:
                </p>
                <button
                    type="button"
                    onClick={() => {
                        posthog.capture('miniapp_share_number_otp_fallback')
                        // `phone` is guaranteed here: stage only becomes
                        // 'fallback' when we actually have a number.
                        if (phone) onUseOtp(phone)
                    }}
                    className="w-full bg-green-1 text-white rounded-md py-2 text-sm"
                >
                    ورود با کد پیامکی
                </button>
            </div>
        )
    }

    return (
        <button
            type="button"
            onClick={share}
            disabled={disabled}
            className="w-full mt-3 border border-green-1 text-green-1 rounded-md py-2 text-sm disabled:opacity-60"
        >
            استفاده از شمارهٔ همین حساب
        </button>
    )
}
